import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import Chrome from "@zendesk/knowledge-chrome/lib/Chrome";
import { ContentView, Sidebar } from "@zendesk/guide-chrome";
import {
  getCurrentBrandId,
  hasFeature,
  getHelpCenter,
} from "@zendesk/knowledge-context";
import Gap from "@zendesk/knowledge-components/lib/Gap";
import Breadcrumbs from "@zendesk/knowledge-components/lib/Breadcrumbs";
import t from "@zendesk/client-i18n-tools";
import { Button } from "@zendeskgarden/react-buttons";
import { DragDropContext } from "react-beautiful-dnd";
import createBrandedUrl from "@zendesk/knowledge-utils/lib/createBrandedUrl";
import * as actions from "../../actions/section";
import ErrorBoundary from "../ErrorBoundary";
import LoadingOverlay from "../LoadingOverlay";
import SectionList from "../SectionList";
import ArticleList from "../ArticleList";
import Empty from "../Empty";
import Notifications from "../Notifications";
import { pathForBreadcrumb } from "../../utils/pathForBreadcrumb";
import styles from "./index.css";

export class Section extends Component {
  constructor(props) {
    super(props);

    this.onSectionPageSelected = this.onSectionPageSelected.bind(this);
    this.onArticlePageSelected = this.onArticlePageSelected.bind(this);
    this.openNewSectionPage = this.openNewSectionPage.bind(this);
    this.openNewArticlePage = this.openNewArticlePage.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onBreadcrumbClick = this.onBreadcrumbClick.bind(this);
    this.onSectionMoveToTop = this.onSectionMoveToTop.bind(this);
    this.onArticleMoveToTop = this.onArticleMoveToTop.bind(this);
  }

  componentDidMount() {
    const { dispatch, sectionPage, articlePage, sectionId } = this.props;
    dispatch(actions.fetchSection(sectionId, sectionPage, articlePage));
  }

  componentDidUpdate(prevProps) {
    const { dispatch, sectionPage, articlePage, sectionId } = this.props;

    if (prevProps.sectionId !== sectionId) {
      dispatch(actions.fetchSection(sectionId, sectionPage, articlePage));
    }
  }

  openNewSectionPage() {
    const { sectionId } = this.props;

    window.open(
      `/knowledge/sections/new?brand_id=${getCurrentBrandId()}&parent_section_id=${sectionId}`,
      "_blank"
    );
  }

  openNewArticlePage() {
    const { sectionId } = this.props;
    window.open(
      `/knowledge/articles/new?brand_id=${getCurrentBrandId()}&section_id=${sectionId}`,
      "_blank"
    );
  }

  onSectionPageSelected(page) {
    const { dispatch, sectionId, articlePage } = this.props;

    dispatch(actions.fetchSections(sectionId, { page }));

    dispatch(
      push({
        pathname: `/knowledge/arrange/sections/${sectionId}/section_page/${page}/article_page/${articlePage}`,
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onArticlePageSelected(page) {
    const { dispatch, sectionId, sectionPage } = this.props;

    dispatch(actions.fetchArticles(sectionId, { page }));

    dispatch(
      push({
        pathname: `/knowledge/arrange/sections/${sectionId}/section_page/${sectionPage}/article_page/${page}`,
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onDragEnd(dragEnd) {
    const { combine, source, destination, type, draggableId } = dragEnd;
    const { dispatch, sectionId } = this.props;

    const isPositioning =
      destination &&
      !(
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      );

    if (!isPositioning && !combine) {
      return;
    }

    if (combine) {
      dispatch(
        actions.moveSectionToSection(
          draggableId, // moved section
          combine.draggableId, // destination section
          source.index
        )
      );
    } else if (type === "article" && destination.droppableId === "articles") {
      dispatch(
        actions.positionArticle(
          sectionId,
          draggableId,
          source.index,
          destination.index
        )
      );
    } else if (type === "article") {
      const [droppableType, droppableId] = destination.droppableId.split("-");

      if (droppableType === "section" && droppableId) {
        dispatch(
          actions.moveArticleToSection(
            draggableId,
            Number(droppableId),
            source.index
          )
        );
      }
    } else if (type === "section" && destination.droppableId === "sections") {
      dispatch(
        actions.positionSection(
          sectionId,
          draggableId,
          source.index,
          destination.index
        )
      );
    }
  }

  onBreadcrumbClick(item) {
    const { dispatch } = this.props;

    dispatch(
      push({
        pathname: pathForBreadcrumb(item),
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onSectionMoveToTop(section, sourceIndex) {
    const { dispatch } = this.props;
    dispatch(actions.moveSectionToTop(section, sourceIndex));
  }

  onArticleMoveToTop(article, sourceIndex) {
    const { dispatch } = this.props;
    dispatch(actions.moveArticleToTop(article, sourceIndex));
  }

  renderBreadcrumbs() {
    const { breadcrumbs, isFetching } = this.props;

    if (isFetching) {
      return null;
    }

    return (
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={this.onBreadcrumbClick}
      />
    );
  }

  renderContent() {
    const {
      articlePage,
      sectionPage,
      articleCount,
      sectionCount,
      isFetching,
    } = this.props;

    if (isFetching) {
      return null;
    }

    if (sectionCount === 0 && articleCount === 0) {
      return (
        <Empty>
          <Empty.Title>{t("knowledge-arrange.section.empty")}</Empty.Title>
          <Empty.Description>
            {t("knowledge-arrange.section.emptyDescription")}
          </Empty.Description>
          <Empty.Actions>
            <Button
              size="small"
              data-test-id="empty-add-section"
              onClick={this.openNewSectionPage}
            >
              {t("knowledge-arrange.sections.addSection")}
            </Button>
            <Gap.Medium />
            <Button
              primary
              size="small"
              data-test-id="empty-add-article"
              onClick={this.openNewArticlePage}
            >
              {t("knowledge-arrange.articles.addArticle")}
            </Button>
          </Empty.Actions>
        </Empty>
      );
    }

    return (
      <div>
        <SectionList
          onMoveToTop={this.onSectionMoveToTop}
          onPageSelected={this.onSectionPageSelected}
          page={sectionPage}
          count={sectionCount}
        />
        <ArticleList
          onMoveToTop={this.onArticleMoveToTop}
          onPageSelected={this.onArticlePageSelected}
          page={articlePage}
          count={articleCount}
        />
      </div>
    );
  }

  render() {
    const { hasCommunity } = this.props;
    const hc = getHelpCenter();

    return (
      <Chrome
        brandId={getCurrentBrandId()}
        dir={t.dir}
        topNav={
          <Chrome.Breadcrumbs>
            <Chrome.Breadcrumbs.Item>
              <a href={`/knowledge/arrange?brand_id=${getCurrentBrandId()}`}>
                {t("knowledge-arrange.breadcrumbs.arrangeContent")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              {t("knowledge-arrange.name")}
            </Chrome.Breadcrumbs.Item>
          </Chrome.Breadcrumbs>
        }
      >
        <Sidebar>
          <Sidebar.Item active testId="sidebar-user-segments-link">
            {t("knowledge-arrange.name")}
          </Sidebar.Item>

          {hasCommunity && (
            <Sidebar.LinkItem
              href={createBrandedUrl(
                hc.route.subdomain,
                "/hc/admin/arrange_contents#community"
              )}
              testId="sidebar-arrange-topics-link"
            >
              {t("knowledge-arrange.sidebar.arrangeTopics")}
            </Sidebar.LinkItem>
          )}

          <Sidebar.LinkItem
            href={createBrandedUrl(
              hc.route.subdomain,
              "/hc/admin/import_articles"
            )}
            testId="sidebar-import-articles-link"
          >
            {t("knowledge-arrange.sidebar.importArticles")}
          </Sidebar.LinkItem>
        </Sidebar>

        <ContentView>
          <ErrorBoundary>
            <Notifications dir={t.dir} />
            <div className={styles.appHeader}>
              <h1 className={styles.title}>{t("knowledge-arrange.name")}</h1>
            </div>

            {this.renderBreadcrumbs()}
            <DragDropContext onDragEnd={this.onDragEnd}>
              {this.renderContent()}
            </DragDropContext>

            <LoadingOverlay />
          </ErrorBoundary>
        </ContentView>
      </Chrome>
    );
  }
}

Section.propTypes = {
  dispatch: PropTypes.func,
  sectionId: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  sectionCount: PropTypes.number.isRequired,
  sectionPage: PropTypes.number.isRequired,
  articleCount: PropTypes.number.isRequired,
  articlePage: PropTypes.number.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  hasCommunity: PropTypes.bool,
};

const mapStateToProps = (
  { articles, section, sections, overlay },
  { match }
) => {
  const sectionPage = parseInt(match.params.sectionPage, 10);
  const articlePage = parseInt(match.params.articlePage, 10);

  return {
    sectionId: match.params.id,
    isFetching: section.isFetching,
    articleCount: section.articleCount,
    sectionCount: section.sectionCount,
    breadcrumbs: section.breadcrumbs,
    sectionPage: isNaN(sectionPage) ? 1 : sectionPage,
    articlePage: isNaN(articlePage) ? 1 : articlePage,
    hasCommunity: hasFeature("community") && getHelpCenter().community,
  };
};

export default connect(mapStateToProps)(Section);



// WEBPACK FOOTER //
// ./src/components/Section/index.js